import { jsx as _jsx } from "react/jsx-runtime";
/**
 * A set of keys that components report while they hold them. The set and the actions live in two
 * contexts, so a change to the set re-renders its readers and not each reporter. The undrawn-mesh
 * list and the empty-shape list each make one. With no provider, a report does nothing and the set
 * is empty.
 */
import { createContext, useContext, useEffect, useMemo } from 'react';
import { useCountedReports } from '../hooks/useCountedReports.js';
const NO_ACTIONS = { report: () => { }, clear: () => { } };
const NO_KEYS = new Set();
export function createCountedReportsContext(displayName) {
    const ActionsContext = createContext(NO_ACTIONS);
    ActionsContext.displayName = `${displayName}Actions`;
    const ReportedContext = createContext(NO_KEYS);
    ReportedContext.displayName = displayName;
    function Provider({ children }) {
        const { reported, report, clear } = useCountedReports();
        const actions = useMemo(() => ({ report, clear }), [report, clear]);
        return (_jsx(ActionsContext.Provider, { value: actions, children: _jsx(ReportedContext.Provider, { value: reported, children: children }) }));
    }
    /** Holds one report of `key` while the caller is mounted. Null reports nothing. */
    function useReportWhile(key) {
        const { report, clear } = useContext(ActionsContext);
        useEffect(() => {
            if (key === null)
                return undefined;
            report(key);
            return () => clear(key);
        }, [key, report, clear]);
    }
    return {
        Provider,
        useReportWhile,
        /** Each key with at least one open report. */
        useReported: () => useContext(ReportedContext),
    };
}
//# sourceMappingURL=countedReportsContext.js.map